import React, { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button/Button';
import ImageCropper from '../ImageCropper/ImageCropper';
import { useAnimatedVisibility } from '../../../hooks/useAnimatedVisibility';
import styles from './ImageUploadModal.module.css';

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void | Promise<void>;
  title: string;
  aspectRatio?: string;
  recommendedSize?: string;
  allowedTypes?: string[];
  getTranslation?: (key: string) => string;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  title,
  aspectRatio,
  recommendedSize,
  allowedTypes = ['jpg', 'jpeg', 'png'],
  getTranslation
}) => {
  const t = (key: string, fallback: string) => getTranslation ? getTranslation(key) || fallback : fallback;
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [step, setStep] = useState<'select' | 'crop' | 'preview'>('select');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewObjectUrlRef = useRef<string | null>(null);

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedTypes.includes(fileExtension)) {
      return;
    }

    setSelectedFile(file);

    // If cropper is enabled for this usage, go to crop step first
    if (aspectRatio) {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      setPreview(null);
      setIsLoading(false);
      setStep('crop');
      return;
    }

    setIsLoading(true);

    // Create preview (data URL)
    const reader = new FileReader();
    reader.onload = (e) => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      setPreview(e.target?.result as string);
      setIsLoading(false);
      setStep('preview');
    };
    reader.onerror = () => {
      setIsLoading(false);
    };
    reader.readAsDataURL(file);
  }, [allowedTypes, aspectRatio]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleClose = useCallback(() => {
    setPreview(null);
    setSelectedFile(null);
    setIsDragging(false);
    setIsLoading(false);
    setIsUploading(false);
    setStep('select');
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (selectedFile && !isLoading && !isUploading) {
      setIsUploading(true);
      try {
        await Promise.resolve(onUpload(selectedFile));
        handleClose();
      } catch {
        setIsUploading(false);
        // Don't close modal on error so user can try again
      }
    }
  }, [selectedFile, onUpload, handleClose, isLoading, isUploading]);

  const { shouldRender, isClosing, onAnimationEnd } = useAnimatedVisibility({
    isOpen,
    closeDuration: 300,
  });

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={`${styles.modalOverlay} ${isClosing ? styles.modalOverlayClosing : ''}`}
      onClick={handleClose}
      onAnimationEnd={onAnimationEnd}
    >
      <div
        className={`${styles.modalContent} ${isClosing ? styles.modalContentClosing : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{title}</h3>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close"
            disabled={isLoading || isUploading}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className={styles.modalBody}>
          {step === 'crop' && selectedFile && aspectRatio ? (
            <ImageCropper
              file={selectedFile}
              aspectRatio={aspectRatio}
              getTranslation={getTranslation}
              onCancel={() => {
                setSelectedFile(null);
                setPreview(null);
                setStep('select');
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              onSubmit={(edited, previewUrl) => {
                if (previewObjectUrlRef.current) {
                  URL.revokeObjectURL(previewObjectUrlRef.current);
                }
                previewObjectUrlRef.current = previewUrl;
                setSelectedFile(edited);
                setPreview(previewUrl);
                setStep('preview');
              }}
            />
          ) : (
            <div
              className={`${styles.dropZone} ${aspectRatio ? styles.dropZoneWithAspect : ''} ${isDragging ? styles.dropZoneActive : ''} ${preview ? styles.dropZoneWithPreview : ''} ${isLoading || isUploading ? styles.dropZoneLoading : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={isLoading || isUploading ? undefined : handleClick}
              style={{
                cursor: isLoading || isUploading ? 'wait' : 'pointer',
                ...(aspectRatio ? { aspectRatio: aspectRatio.replace(':', ' / ') } : {})
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={allowedTypes.map(t => `.${t}`).join(',')}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />

              {isLoading || isUploading ? (
                <div className={`${styles.loadingContainer} ${aspectRatio ? styles.loadingContainerWithAspect : ''}`}>
                  <div className={styles.loadingSpinner}></div>
                  <p className={styles.loadingText}>
                    {isLoading
                      ? t('imageUpload.loading', 'Loading image...')
                      : t('imageUpload.uploading', 'Uploading image...')
                    }
                  </p>
                </div>
              ) : preview ? (
                <div className={`${styles.previewContainer} ${aspectRatio ? styles.previewContainerWithAspect : ''}`}>
                  <img src={preview} alt="Preview" className={styles.previewImage} />
                  <div className={styles.previewOverlay}>
                    <span className={styles.changeText}>{t('imageUpload.clickToChange', 'Click to change image')}</span>
                  </div>
                </div>
              ) : (
                <div className={styles.dropZoneContent}>
                  <div className={styles.uploadIcon}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p className={styles.dropZoneText}>
                    {t('imageUpload.dragAndDrop', 'Drag and drop an image here, or click to select')}
                  </p>
                  <div className={styles.dropZoneInfo}>
                    {aspectRatio && (
                      <span className={styles.infoItem}>
                        {t('imageUpload.aspectRatio', 'Aspect Ratio')}: {aspectRatio}
                      </span>
                    )}
                    {recommendedSize && (
                      <span className={styles.infoItem}>
                        {t('imageUpload.recommendedSize', 'Recommended Size')}: {recommendedSize}
                      </span>
                    )}
                    <span className={styles.infoItem}>
                      {t('imageUpload.allowedFormats', 'Formats')}: {allowedTypes.map(t => t.toUpperCase()).join(', ')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {step !== 'crop' && (
          <div className={styles.modalFooter}>
            <Button
              variant="secondary"
              size="medium"
              onClick={handleClose}
              disabled={isLoading || isUploading}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleConfirm}
              disabled={!selectedFile || isLoading || isUploading || step !== 'preview'}
            >
              {isLoading || isUploading
                ? t('imageUpload.loadingButton', 'Loading...')
                : t('imageUpload.upload', 'Upload')
              }
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ImageUploadModal;

